<script setup lang="ts">
import { useTemplateRef, ref, computed } from 'vue'
import BaseModal from '@/components/base/BaseModal.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import GameCardItem from '@/components/game-play/GameCardItem.vue'
import ArrowFatLeftIcon from '@/assets/svgs/icons/arrow-fat-left.svg'
import ArrowFatRightIcon from '@/assets/svgs/icons/arrow-fat-right.svg'
import DiamondImage from '@/assets/svgs/cards/Diamond.svg'
import HeartImage from '@/assets/svgs/cards/Heart.svg'
import ClubImage from '@/assets/svgs/cards/Clubs.svg'
import SpadeImage from '@/assets/svgs/cards/Spade.svg'
import type { CardT } from '@/types/Game'

const modalRef = useTemplateRef('modalRef')

const open = () => {
  modalRef.value?.open()
}

const close = () => {
  modalRef.value?.close()
}

defineExpose({
  open,
  close
})

const currentStep = ref(0)
const stepContent = [
  {
    content: 'Every player gets 7 random cards from the deck.',
    subtitle: 'Each card has either <span class="font-bold text-inherit">PLAN</span>, <span class="font-bold text-inherit">SAVE</span> or <span class="font-bold text-inherit">INVEST</span> at the center'
  },
  {
    title: 'Each card has pips similar to a standard deck of cards',
    subtitle: 'Every pip or icon will have either letters or an asterisk "*"'
  },
  {
    content:
      'The goal of the game is to form the words <span class="font-bold text-inherit">PLAN</span>, <span class="font-bold text-inherit">SAVE</span> or <span class="font-bold text-inherit">INVEST</span> with the letters in each pip and matching card type'
  },
  {
    content: 'You can only form words with the same matching cards.',
    subtitle: '"V" from card <span class="font-bold text-inherit">SAVE</span> cannot be used to spell <span class="font-bold text-inherit">INVEST</span>.'
  },
  {
    content: 'After spelling a word, your point is the total sum of the card numbers above the pip.',
    subtitle: 'Court cards like K, Q, J and A carry 10 points.'
  }
]

const nextStep = () => {
  if (currentStep.value < stepContent.length - 1) {
    currentStep.value++
  }
}

const prevStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--
  }
}

const goToStep = (step: number) => {
  currentStep.value = step
}

const canGoPrev = computed(() => currentStep.value > 0)
const canGoNext = computed(() => currentStep.value < stepContent.length - 1)

const demoCard: CardT = {
  id: 'random-id',
  type: 'Pay yourself first',
  rank: '8',
  suit: 'spades',
  value: 10,
  imageUrl: null,
  letter: 'V',
  text: 'Avoid sliding into bankruptcy or poverty in the event of unforeseen expenses; create an emergency fund.',
  category: 'save'
}
</script>

<template>
  <BaseModal ref="modalRef" class="!px-5 !py-6 w-max">
    <div class="flex flex-col gap-y-2 items-center">
      <h2 class="text-2xl font-bold text-white">How to play</h2>
      <div class="py-2 pr-2.5 pl-5 bg-[#FEFEFE] rounded-xl grid grid-cols-[250px_max-content] gap-x-5 items-center">
        <div class="flex flex-col gap-y-10">
          <p v-if="stepContent[currentStep].content" class="text-lg font-medium text-[#082552]" v-html="stepContent[currentStep].content" />

          <div v-if="currentStep === 1" class="flex flex-col gap-y-2">
            <p class="text-lg font-medium text-[#082552]">{{ stepContent[currentStep].title }}</p>
            <div class="flex gap-x-2 items-center">
              <DiamondImage class="h-4 *:fill-[#BC1E24]" />
              <HeartImage class="h-4 *:fill-[#BC1E24]" />
              <SpadeImage class="h-4 *:fill-[#211C1D]" />
              <ClubImage class="h-4 *:fill-[#211C1D]" />
            </div>
          </div>

          <p v-if="stepContent[currentStep].subtitle" class="text-lg font-medium text-[#082552]" v-html="stepContent[currentStep].subtitle" />
        </div>
        <div class="rounded-2xl bg-[#193A2D] py-6 px-12 overflow-hidden">
          <GameCardItem :card="demoCard" :key="demoCard.id" :class="[{ 'scale-125 translate-y-[33%]': currentStep !== 0 }, 'transition-transform duration-500 ease-out']" />
        </div>
      </div>
      <div class="w-full grid grid-cols-1 gap-y-[5.5px]">
        <div class="mt-[0.5px] flex gap-x-[5px] items-center justify-center">
          <button
            v-for="step in stepContent.length"
            :key="step - 1"
            class="w-2.5 h-2.5 rounded-full transition-colors duration-200"
            :class="currentStep === step - 1 ? 'bg-[#FF8B00]' : 'bg-[#FFE8CC] hover:bg-[#FF8B00]/70'"
            @click="goToStep(step - 1)"
          />
        </div>
        <div class="flex justify-between items-center">
          <BaseButton background-color="#F88284" border-color="#471F20" class="w-11 lg:w-12 h-9 lg:h-10 !p-0 justify-center" :disabled="!canGoPrev" @click="prevStep">
            <ArrowFatLeftIcon />
          </BaseButton>
          <BaseButton background-color="#F88284" border-color="#471F20" class="w-11 lg:w-12 h-9 lg:h-10 !p-0 justify-center" :disabled="!canGoNext" @click="nextStep">
            <ArrowFatRightIcon />
          </BaseButton>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
